import type { AnatomyBinding, AnatomyVariant } from "./componentAnatomy";
import { anatomyTextStyle } from "./componentAnatomyText";

/**
 * @purpose Button's colour layer, one block per variant of `Button.module.scss`
 * (extraction §1). No colour in that sheet varies by size, so nothing here is
 * duplicated per size; the few variants that re-declare a text style at one
 * size carry it in `sizeBindings`, mirroring their compound `.variant.size`
 * block.
 */

const fill = (token: `--${string}`): AnatomyBinding => ({
    partId: "background",
    property: "background-color",
    token,
    via: "--button-bg",
});

const edge = (token: `--${string}`): AnatomyBinding => ({
    partId: "border",
    property: "border-color",
    token,
    via: "--button-border",
});

const text = (token: `--${string}`): AnatomyBinding => ({
    partId: "label",
    property: "color",
    token,
    via: "--button-fg",
});

const transparentFill: AnatomyBinding = {
    partId: "background",
    property: "background-color",
    literal: "transparent",
    via: "--button-bg",
    note: "The .button default; this variant declares no fill of its own.",
};

const transparentEdge: AnatomyBinding = {
    partId: "border",
    property: "border-color",
    literal: "transparent",
    via: "--button-border",
    note: "Width is still reserved so gaining a border never shifts the label by 1px.",
};

// One ring for every variant: accent-focus border plus the blue-100 glow.
const focusBindings: readonly AnatomyBinding[] = [
    edge("--border-accent-focus"),
    {
        partId: "focusRing",
        property: "box-shadow (spread)",
        token: "--border-width-thick",
    },
    {
        partId: "focusRing",
        property: "box-shadow (color)",
        token: "--focus-ring-color",
    },
];

const ghostLabel = anatomyTextStyle({
    scale: "s",
    weight: "400",
    mixin: "text-s-14-regular",
    note: "The xs-medium label style swaps this whole style for text-xs-12-medium.",
});

const accentLabel = anatomyTextStyle({
    scale: "s",
    weight: "400",
    mixin: "text-s-14-regular",
    note: "Figma tags the label Medium but renders S 14 Regular; the sheet follows the render.",
});

const destructiveSmLabel = anatomyTextStyle({
    scale: "s",
    weight: "500",
    mixin: "text-s-14-medium",
    note: "Destructive/24 renders S 14 Medium where every other variant at 24 is XS 12 Medium.",
});

const linkSmLabel = anatomyTextStyle({
    scale: "s",
    weight: "500",
    mixin: "text-s-14-medium",
    note: "Link/24 renders S 14 Medium, overriding the size's XS 12 Medium.",
});

export const buttonVariantSpecs: readonly AnatomyVariant[] = [
    {
        id: "primary",
        label: "Primary",
        selector: ".primary",
        restingBindings: [
            fill("--bg-primary"),
            edge("--border-default"),
            text("--fg-primary"),
        ],
        stateBindings: {
            hover: [edge("--border-hover")],
            pressed: [
                fill("--bg-secondary"),
                edge("--border-hover"),
                text("--fg-secondary"),
            ],
            focus: focusBindings,
            disabled: [edge("--border-disabled"), text("--fg-disabled")],
        },
    },
    {
        id: "secondary",
        label: "Secondary",
        selector: ".secondary",
        restingBindings: [
            transparentFill,
            transparentEdge,
            text("--fg-primary"),
        ],
        stateBindings: {
            hover: [fill("--bg-neutral-hover")],
            pressed: [fill("--bg-neutral-pressed"), text("--fg-secondary")],
            focus: [...focusBindings, fill("--bg-primary")],
            disabled: [text("--fg-disabled")],
        },
    },
    {
        id: "tertiary",
        label: "Tertiary",
        selector: ".tertiary",
        restingBindings: [
            fill("--bg-accent"),
            transparentEdge,
            text("--fg-on-solid"),
        ],
        stateBindings: {
            hover: [fill("--bg-accent-hover")],
            pressed: [fill("--bg-accent-pressed")],
            focus: [...focusBindings, fill("--bg-accent")],
            disabled: [fill("--bg-disabled"), text("--fg-tertiary")],
        },
    },
    {
        id: "destructive",
        label: "Destructive",
        selector: ".destructive",
        restingBindings: [
            fill("--bg-primary"),
            edge("--border-error"),
            text("--fg-error"),
        ],
        stateBindings: {
            hover: [edge("--border-error-hover")],
            pressed: [
                fill("--bg-error-outline-pressed"),
                edge("--border-error-hover"),
            ],
            focus: focusBindings,
            disabled: [
                edge("--border-error-disabled"),
                text("--fg-error-disabled"),
            ],
        },
        sizeBindings: { sm: destructiveSmLabel },
    },
    {
        id: "ghost",
        label: "Ghost",
        selector: ".ghost",
        restingBindings: [
            transparentFill,
            transparentEdge,
            text("--fg-primary"),
            ...ghostLabel,
        ],
        stateBindings: {
            hover: [edge("--border-hover")],
            pressed: [edge("--border-hover"), text("--fg-secondary")],
            focus: [...focusBindings, fill("--bg-primary")],
            disabled: [
                fill("--bg-primary"),
                edge("--border-disabled"),
                text("--fg-disabled"),
            ],
        },
    },
    {
        id: "accent",
        label: "Accent",
        selector: ".accent",
        restingBindings: [
            fill("--bg-primary"),
            edge("--border-accent"),
            text("--fg-accent"),
            ...accentLabel,
        ],
        stateBindings: {
            hover: [edge("--border-accent-hover")],
            pressed: [
                fill("--bg-accent-outline-pressed"),
                edge("--border-accent-hover"),
                text("--fg-accent-pressed"),
            ],
            focus: focusBindings,
            disabled: [
                edge("--border-accent-disabled"),
                text("--fg-accent-disabled"),
            ],
        },
    },
    {
        id: "link",
        label: "Link",
        selector: ".link",
        restingBindings: [
            transparentFill,
            transparentEdge,
            text("--fg-primary"),
            {
                partId: "background",
                property: "padding-inline",
                literal: "0",
                via: "--button-padding-x",
                note: "Link is padding-less at every size, overriding the size's own step.",
            },
        ],
        stateBindings: {
            hover: [text("--fg-secondary")],
            pressed: [text("--fg-secondary")],
            focus: [
                ...focusBindings,
                fill("--bg-primary"),
                text("--fg-primary"),
            ],
            disabled: [text("--fg-disabled")],
        },
        sizeBindings: {
            xs: [
                {
                    partId: "border",
                    property: "border-radius",
                    token: "--radius-m",
                    via: "--button-radius",
                    note: "Link/20 keeps --radius-m where every other 20px button drops to --radius-s.",
                },
            ],
            sm: linkSmLabel,
        },
    },
];
